import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface CreateRolePayload {
  name: string;
  password: string;
}

export interface UpdateRolePayload {
  id: number;
  name: string;
  password?: string;
}

export interface RoleEntity {
  id: number;
  name: string;
}

export interface CreateRoleResponse {
  success: boolean;
  message: string;
  role: RoleEntity;
}

export interface UpdateRoleResponse {
  success: boolean;
  message: string;
  role: RoleEntity;
}

export interface RoleUser {
  id: number;
  name: string;
  mobile_number: string;
  email: string | null;
  is_staff: boolean;
  is_superuser: boolean;
  is_active: boolean;
  commission_percentage: number | null;
  app_name: string | null;
  created_at: string;
  last_login: string | null;
}

export interface RoleWithUsers {
  id: number;
  name: string;
  user_count: number;
  active_user_count: number;
  users: RoleUser[];
}

export interface RolesState {
  createRoleLoading: boolean;
  createRoleError: string | null;
  lastCreatedRole: CreateRoleResponse | null;

  updateRoleLoading: boolean;
  updateRoleError: string | null;
  lastUpdatedRole: UpdateRoleResponse | null;

  fetchWithUsersLoading: boolean;
  fetchWithUsersError: string | null;
  rolesWithUsers: RoleWithUsers[];
}

const initialState: RolesState = {
  createRoleLoading: false,
  createRoleError: null,
  lastCreatedRole: null,

  updateRoleLoading: false,
  updateRoleError: null,
  lastUpdatedRole: null,

  fetchWithUsersLoading: false,
  fetchWithUsersError: null,
  rolesWithUsers: [],
};

export const createRole = createAsyncThunk(
  'roles/createRole',
  async (payload: CreateRolePayload, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/roles/', payload);
      return response.data as CreateRoleResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to create role');
    }
  }
);

export const updateRole = createAsyncThunk(
  'roles/updateRole',
  async (payload: UpdateRolePayload, { rejectWithValue }) => {
    try {
      const response = await api.put('/admin/roles/', payload);
      return response.data as UpdateRoleResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to update role');
    }
  }
);

export const fetchRolesWithUsers = createAsyncThunk(
  'roles/fetchRolesWithUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/roles/', {
        params: { include_users: true },
      });
      // Expecting { success, message, roles: [...] }
      if (Array.isArray(response.data?.roles)) {
        return response.data.roles as RoleWithUsers[];
      }
      // Fallbacks in case API returns array directly
      if (Array.isArray(response.data)) {
        return response.data as RoleWithUsers[];
      }
      return [] as RoleWithUsers[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || 'Failed to fetch roles with users');
    }
  }
);

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearCreateRoleError: (state) => {
      state.createRoleError = null;
    },
    clearLastCreatedRole: (state) => {
      state.lastCreatedRole = null;
    },
    clearUpdateRoleError: (state) => {
      state.updateRoleError = null;
    },
    clearLastUpdatedRole: (state) => {
      state.lastUpdatedRole = null;
    },
    clearFetchRolesWithUsersError: (state) => {
      state.fetchWithUsersError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createRole.pending, (state) => {
        state.createRoleLoading = true;
        state.createRoleError = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.createRoleLoading = false;
        state.lastCreatedRole = action.payload;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.createRoleLoading = false;
        state.createRoleError = action.payload as string;
      });

    builder
      .addCase(updateRole.pending, (state) => {
        state.updateRoleLoading = true;
        state.updateRoleError = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.updateRoleLoading = false;
        state.lastUpdatedRole = action.payload;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.updateRoleLoading = false;
        state.updateRoleError = action.payload as string;
      });

    builder
      .addCase(fetchRolesWithUsers.pending, (state) => {
        state.fetchWithUsersLoading = true;
        state.fetchWithUsersError = null;
      })
      .addCase(fetchRolesWithUsers.fulfilled, (state, action) => {
        state.fetchWithUsersLoading = false;
        state.rolesWithUsers = action.payload;
      })
      .addCase(fetchRolesWithUsers.rejected, (state, action) => {
        state.fetchWithUsersLoading = false;
        state.fetchWithUsersError = action.payload as string;
      });
  },
});

export const { 
  clearCreateRoleError, 
  clearLastCreatedRole,
  clearUpdateRoleError,
  clearLastUpdatedRole,
  clearFetchRolesWithUsersError,
} = rolesSlice.actions;

export default rolesSlice.reducer;






